import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Popup } from "@progress/kendo-react-popup";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { updateCurrUser } from "../../actions/auth";
import { setContentManagerDataDetails, setContentManagerDataView, setFileMetaData, setSelectedContentItem } from "../../actions/contentManager";
import { icon, label, notifyIcon, number, quote, route } from "../../config";
import { createDynamicTeamName, getNotification } from "../../utils/common";
import "./bookmarks.scss";

/**
 * BookmarkPopup Component
 * Renders a popup when hovering over a bookmarked item
 * Parent Component: MarkBookmark
 * @param {Object} props - Component properties
 * @author Dimple Sahota
 */
const BookmarkPopup = ({
  popupItem,
  dataItem,
  type,
  setHoveredItem,
  setHoveredBookmark,
  setType,
  onPopupMouseEnter,
  onPopupMouseLeave,
  showAnimation,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { selectedContentManagerItem } = useSelector((state) => state.contentManager);
  const { id, operationalTeamName } = useSelector((state) => state.auth.user);
  const allProjectsList = useSelector((state) => state.tasks.defaultDetails.allProjectsList);
  const pathname = location.pathname;

  /**
   * Hides the bookmark popup
   */
  const hidePopUp = () => {
    setHoveredItem(null);
    setHoveredBookmark(null);
  };

  /**
   * Changes view with access verification
   */
  const changeTaskView = async (viewType) => {
    if (viewType === label.ANALYTICS) return;
    const project = allProjectsList?.find((p) => p.value == dataItem?.Id);

    if (project && project.QueueId && !(project.owners + "," + project.collaborators).includes(id)) {
      getNotification(createDynamicTeamName(quote.PROJECT_ACCESS_DENIED, operationalTeamName), notifyIcon.WARNING_ICON);
      hidePopUp();
      return;
    }

    if (type === "Queue") {
      history.push(route.PRIVATE_ROUTE.QUEUE_TASKS.PATH + "/" + dataItem?.Id + "/" + encodeURIComponent(dataItem?.Name));
    } else history.push(route.PRIVATE_ROUTE.TASKS.PATH + "/" + dataItem?.Id + "/" + encodeURIComponent(dataItem?.Name));
    if (viewType) await dispatch(updateCurrUser({ lastSelectedBoard: viewType }));
    hidePopUp();
    setType(null);
  };

  /**
   * Changes to Library View with access verification
   */
  const changeToLibraryView = () => {
    const project = allProjectsList?.find((p) => p.value == dataItem?.Id);

    if (project && project.QueueId && !(project.owners + "," + project.collaborators).includes(id)) {
      getNotification(createDynamicTeamName(quote.PROJECT_ACCESS_DENIED, operationalTeamName), notifyIcon.WARNING_ICON);
      hidePopUp();
      return;
    }

    if (pathname.includes(label.LIBRARY_ROUTE) && selectedContentManagerItem?.projectId == dataItem?.Id) return;
    if (dataItem?.Id) {
      history.push(route.PRIVATE_ROUTE.CMS.PATH + "/" + dataItem?.Id + "/" + encodeURIComponent(dataItem?.Name));
    } else history.push(`${route.PRIVATE_ROUTE.CMS.PATH}`);
    dispatch(setSelectedContentItem([]));
    dispatch(setFileMetaData({}));
    dispatch(setContentManagerDataView(label.CMS_GRID));
    dispatch(setContentManagerDataDetails([]));
    hidePopUp();
    setType(null);
  };

  /**
   * Changes to Email View with access verification
   */
  const changeToEmailView = () => {
    const project = allProjectsList?.find((p) => p.value == dataItem?.Id);

    if (project && project.QueueId && !(project.owners + "," + project.collaborators).includes(id)) {
      getNotification(createDynamicTeamName(quote.PROJECT_ACCESS_DENIED, operationalTeamName), notifyIcon.WARNING_ICON);
      hidePopUp();
      return;
    }

    if (dataItem?.Id) {
      history.push(route.PRIVATE_ROUTE.EMAILS.PATH + "/" + dataItem?.Id + "/" + encodeURIComponent(dataItem?.Name));
    } else history.push(`${route.PRIVATE_ROUTE.EMAILS.PATH}`);
    hidePopUp();
    setType(null);
  };

  /**
   * Renders an icon button inside the BookmarkPopup.
   * Displays a tooltip if `isTooltip` is true.
   *
   * @param {string} viewType - The type of view to navigate to when clicked.
   * @param {JSX.Element} iconType - The icon component to be displayed.
   * @param {string} text - The label text for the icon.
   * @param {boolean} isTooltip - Determines whether to wrap the icon in a tooltip.
   * @returns {JSX.Element} - A clickable icon button with optional tooltip.
   * @author Dimple Sahota
   */
  const renderIcon = (viewType, iconType, text, isTooltip) => (
    <div className={iconType === icon.GRID_ICON ? "bookmark-popup-icons" : "bookmark-popup-icons mt-1"}>
      {isTooltip ? (
        <Tooltip anchorElement='target' parentTitle position='bottom'>
          <div
            className={iconType === icon.GRID_ICON ? "bookmark-popup-icon-item p-2 d-flex text-center" : "bookmark-popup-icon-item"}
            title={viewType === label.ANALYTICS ? "Coming Soon" : dataItem?.Id ? text : text}
            onMouseEnter={onPopupMouseEnter}
            onMouseLeave={onPopupMouseLeave}
            onClick={(e) => {
              e.stopPropagation();
              changeTaskView(viewType);
            }}>
            <span className='bookmark-popup-icon'>{iconType}</span>
            <span className='bookmark-popup-text'>{text}</span>
          </div>
        </Tooltip>
      ) : (
        <div
          className={iconType === icon.GRID_ICON ? "bookmark-popup-icon-item p-2 d-flex text-center" : "bookmark-popup-icon-item"}
          onMouseEnter={onPopupMouseEnter}
          onMouseLeave={onPopupMouseLeave}
          onClick={(e) => {
            e.stopPropagation();
            changeTaskView(viewType);
          }}>
          <span className='bookmark-popup-icon'>{iconType}</span>
          <span className='bookmark-popup-text'>{text}</span>
        </div>
      )}
    </div>
  );

  return (
    <Popup
      popupClass={`bookmark-popup-editor overflow-x-hidden overflow-y-auto  ${showAnimation ? "show" : ""}`}
      show={popupItem}
      animate={false}
      collision={{
        horizontal: "fit",
        vertical: "flip",
      }}>
      <div tabIndex={number.ZERO} onMouseEnter={onPopupMouseEnter} onMouseLeave={onPopupMouseLeave}>
        <div className='bookmark-popup-section'>
          {/* Grid View - Visible if NOT a Task */}
          {renderIcon(label.GRID, icon.TASKS, label.GRID, false)}

          {/* Show only if type === "Project" */}
          {type === "Project" && (
            <>
              {/* Pivot View */}
              {renderIcon(label.PIVOT, icon.PIVOT_ICON_BLUE, label.PIVOT, true)}
              {/* Analytics View */}
              {renderIcon(label.ANALYTICS, icon.ANALYTICS_ICON_BLUE, label.ANALYTICS, true)}
              {/* Email View*/}
              <div className='bookmark-popup-icons mt-1'>
                <div
                  className='bookmark-popup-icon-item'
                  onMouseEnter={onPopupMouseEnter}
                  onMouseLeave={onPopupMouseLeave}
                  onClick={(e) => {
                    e.stopPropagation();
                    changeToEmailView();
                  }}>
                  <span className='bookmark-popup-icon'>{icon.EMAIL_ICON}</span>
                  <span className='bookmark-popup-text'>{label.EMAILS}</span>
                </div>
              </div>
              {/* Library */}
              <div className='bookmark-popup-icons mt-1'>
                <div
                  className='bookmark-popup-icon-item'
                  onMouseEnter={onPopupMouseEnter}
                  onMouseLeave={onPopupMouseLeave}
                  onClick={(e) => {
                    e.stopPropagation();
                    changeToLibraryView();
                  }}>
                  <span className='bookmark-popup-icon'>{icon.CLOUD_TEAM}</span>
                  <span className='bookmark-popup-text'>{label.LIBRARY}</span>
                </div>
              </div>
            </>
          )}

          {/* Stream View */}
          {renderIcon(label.STREAM, icon.STREAM_ICON, label.STREAMS, false)}
          {/* Team Canvas */}
          {renderIcon(label.TEAM_CONNECT, icon.TEAM_CONNECT_TEAMS, label.CANVAS, false)}
        </div>
      </div>
    </Popup>
  );
};

export default BookmarkPopup;
